import { Link } from "react-router-dom";
import { playerLink } from "@/components/PlayerCard";

export function MatchRow({ m, kind, t, lang, full = false }) {
  const live = m.status === "IN_PLAY" || m.status === "PAUSED";
  const dt = m.utcDate ? new Date(m.utcDate) : null;
  const locale = lang === "fr" ? "fr-FR" : "en-GB";
  const kickedOff = dt && dt <= new Date();
  // Un résultat ne s'affiche que s'il est CONNU : les feuilles de match écrites avant le
  // 27/09/2026 n'enregistraient pas le score, et « – : – » à la place d'un résultat est pire
  // qu'une date. Chaque match joué depuis porte le sien.
  const hasScore = m.score && (m.score.home !== null || m.score.away !== null);
  const showScore = (kind === "recent" && hasScore) || (live && kickedOff);
  return (
    <div data-testid={kind === "upcoming" ? "match-card-upcoming" : "match-card-result"} className="rounded-lg border border-[#1F293D] bg-[#0B0E14]/70 px-3 py-2.5">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 text-right">
          <span className="truncate text-sm font-medium text-slate-200">{m.home.name}</span>
          {m.home.crest && <img src={m.home.crest} alt="" className="h-5 w-5 shrink-0 object-contain" loading="lazy" />}
        </div>
        <div className="w-24 shrink-0 text-center">
          {showScore ? (
            <span className="font-mono2 text-sm font-bold text-white">
              {m.score.home ?? "–"} : {m.score.away ?? "–"}
              {live && <span className="ml-1.5 rounded bg-[#FF3B30] px-1 py-0.5 font-mono2 text-[9px] font-bold">{t("matches.live")}</span>}
            </span>
          ) : (
            <span className="font-mono2 text-[10px] text-slate-400">
              {dt ? `${dt.toLocaleDateString(locale, { day: "numeric", month: "short" })} ${dt.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}` : "—"}
            </span>
          )}
          <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-600">{m.competition_name}</p>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {m.away.crest && <img src={m.away.crest} alt="" className="h-5 w-5 shrink-0 object-contain" loading="lazy" />}
          <span className="truncate text-sm font-medium text-slate-200">{m.away.name}</span>
        </div>
      </div>
      {(m.rookies || []).length === 0 && m.tracked_rookies > 0 && (
        full && (m.squad_rookies || []).length > 0 ? (
          <div className="mt-1.5 flex flex-wrap justify-center gap-1" data-testid="match-squad-tags">
            {m.squad_rookies.map((r) => (
              <Link
                key={`${r.provider}:${r.player_id}`}
                to={playerLink(r)}
                className="flex items-center gap-1 rounded-full border border-[#1F293D] bg-[#0B0E14] px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-wider text-slate-300 hover:border-[#00FF87]/40 hover:text-[#00FF87]"
                data-testid="match-squad-tag"
              >
                {r.name}
                <span className="rounded bg-[#07090E]/60 px-1 text-[8px] font-bold text-slate-500">{t("matches.squad")}</span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="mt-1.5 text-center font-mono2 text-[9px] uppercase tracking-wider text-slate-500" data-testid="match-tracked-count">
            {t("matches.tracked_rookies", { n: m.tracked_rookies })}
          </p>
        )
      )}
      {(m.rookies || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap justify-center gap-1">
          {(full ? m.rookies : m.rookies.slice(0, 6)).map((r) => {
            const role = r.played && !r.started ? t("matches.sub_on") : r.started ? t("matches.xi") : t("matches.bench");
            return (
              <Link
                key={`${r.provider}:${r.player_id}`}
                to={playerLink(r)}
                className="flex items-center gap-1 rounded-full border border-[#00FF87]/40 bg-[#00FF87]/10 px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-wider text-[#00FF87] hover:bg-[#00FF87]/20"
                data-testid="match-rookie-tag"
              >
                {r.name}
                <span className="rounded bg-[#07090E]/60 px-1 text-[8px] font-bold text-slate-300">{role}</span>
                {r.goals > 0 && <span className="font-bold text-[#FFD600]">{r.goals}{t("player.goals")}</span>}
                {r.assists > 0 && <span className="font-bold text-[#00F0FF]">{r.assists}{t("player.assists")}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Fixtures with a tracked rookie on the sheet only — a panel with zero qualifying
 * fixtures is not rendered, and the whole section disappears when none qualify. */
export default function MatchesStrip({ matches, t, lang }) {
  const panels = [
    { kind: "upcoming", title: t("matches.upcoming"), items: matches.upcoming.slice(0, 3), testid: "matches-upcoming-panel" },
    { kind: "recent", title: t("matches.recent"), items: matches.recent.slice(0, 3), testid: "matches-recent-panel" },
  ].filter((p) => p.items.length > 0);
  if (panels.length === 0) return null;
  return (
    <section className={`mb-8 grid grid-cols-1 gap-4 ${panels.length === 2 ? "lg:grid-cols-2" : ""}`} data-testid="matches-strip">
      {panels.map((p) => (
        <div key={p.kind} className="hud-card rounded-xl p-4" data-testid={p.testid}>
          <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-tight text-white">{p.title}</h2>
          <div className="space-y-2">
            {p.items.map((m) => <MatchRow key={m.match_id} m={m} kind={p.kind} t={t} lang={lang} />)}
          </div>
          <Link to="/matches" className="mt-3 inline-block font-mono2 text-[10px] uppercase tracking-wider text-[#00FF87] hover:underline" data-testid={`see-all-matches-${p.kind}`}>
            {t("matches.see_all")} →
          </Link>
        </div>
      ))}
    </section>
  );
}
